import type { SupabaseClient } from "@supabase/supabase-js";

// Thin Console read model for the A1 Duty Call spine. These reads intentionally stay on
// duty_calls / duty_call_assignments / duty_call_status_events: CAD Live Activity is scan
// history and must never become a second interpretation of the Call trail.

export const DUTY_CALL_LIST_LIMIT = 50;
export const DUTY_CALL_OPEN_LIMIT = 250;
export const DUTY_CALL_TRAIL_LIMIT = 100;

export const DUTY_CALL_LIST_SELECT = [
  "id",
  "site_id",
  "call_ref",
  "call_type_code",
  "origin",
  "open_pool",
  "priority",
  "status",
  "location",
  "note",
  "created_at",
  "updated_at",
].join(",");

export const DUTY_CALL_ASSIGNMENTS_SELECT =
  "duty_call_id,state,linear_status,backup_requested_at,offered_at,expires_at";
export const DUTY_CALL_TYPES_SELECT = "code,family,label";
export const DUTY_CALL_SITES_SELECT = "id,name";

// Coordinate-free by design, and only one direction of that is a database guarantee.
//
// Enforced: duty_call_status_events_gps_dependents_require_coordinates plus
// duty_call_status_events_gps_coordinate_pair make a non-null `gps_captured_at` impossible
// without both coordinates. So "recorded" is never a false positive, and lat/lng never has to
// cross the wire to prove a fix exists.
//
// NOT enforced: the converse. `lat` set with `gps_captured_at` null satisfies every shipped
// CHECK. Only the append-only writer's all-or-nothing behaviour rules it out, which is a
// convention, not a constraint. Until a follow-up expand-only migration requires
// `gps_captured_at` whenever `lat` is present, a row of that shape would read here as "no
// stamp". Reachable only through the service_role INSERT grant or an owner-rights repair.
export const DUTY_CALL_TRAIL_SELECT =
  "event_type,recorded_at,actor_label,note,disposition_code,event_seq,gps_accuracy_meters,gps_captured_at";

export type DutyCallStatus = "open" | "active" | "cleared" | "cancelled";
/** `assigned` is the shipped A1 wire value for dispatch-origin Calls. */
export type DutyCallOrigin = "assigned" | "officer";
export type DutyCallAssignmentState =
  | "offered"
  | "accepted"
  | "declined"
  | "cleared"
  | "cancelled";
export type DutyCallLinearStatus = "starting" | "en_route" | "on_scene";
export type DutyCallEventType =
  | "assigned"
  | "pooled"
  | "claimed"
  | "starting"
  | "en_route"
  | "on_scene"
  | "requesting_backup"
  | "declined"
  /** History fact, not a linear status: an Incident report named this Call. */
  | "incident_attached"
  | "cleared"
  | "cancelled"
  /** Dispatch policy closed an unanswered offer. Written by the private expire-on-touch helper. */
  | "expired";

/**
 * What the trail can prove about one rung's GPS, from coordinate-free columns alone.
 * `absent` means the row stores no stamp — never that a device failed. `unreadable` means the
 * row's GPS fields contradict the append-only writer's all-or-nothing contract.
 */
export type DutyCallGpsProof = "recorded" | "absent" | "unreadable";

export interface DutyCallDisposition {
  code: string;
  label: string;
  shortCode?: string;
}

export interface DutyCallSummary {
  callId: string;
  callRef: string;
  callTypeCode: string;
  family: string;
  type: string;
  priority: 1 | 2 | 3 | 4;
  siteId: string | null;
  site: string;
  location: string | null;
  note: string | null;
  origin: DutyCallOrigin;
  openPool: boolean;
  status: DutyCallStatus;
  assignmentState: DutyCallAssignmentState | null;
  /**
   * Policy hold stamp for a *live* offer only. Null whenever the latest assignment has already
   * responded, so a retained historical `expires_at` can never be read back as a running clock,
   * and null when the account's unanswered-offer hold was off at offer time.
   */
  offerExpiresAt: string | null;
  linearStatus: DutyCallLinearStatus | null;
  backupRequestedAt: string | null;
  assignedAt: string;
  updatedAt: string;
}

export interface DutyCallTrailEvent {
  eventType: DutyCallEventType;
  recordedAt: string;
  actorLabel: string;
  note: string | null;
  dispositionCode: string | null;
  disposition: DutyCallDisposition | null;
  eventSeq: number;
  /** Whether this rung carries a device fix. Never a coordinate. */
  gpsProof: DutyCallGpsProof;
  gpsAccuracyMeters: number | null;
}

export interface DutyCallOfferSite {
  siteId: string;
  label: string;
  live: boolean;
}

export interface DutyCallOfferMember {
  membershipId: string;
  label: string;
}

export interface DutyCallTypeOption {
  code: string;
  family: string;
  label: string;
  defaultPriority: 1 | 2 | 3 | 4;
}

export interface DutyCallOfferOptions {
  sites: DutyCallOfferSite[];
  members: DutyCallOfferMember[];
  callTypes: DutyCallTypeOption[];
}

export interface CreateDutyCallOfferInput {
  siteId: string;
  callTypeCode: string;
  guardMembershipId: string;
  priority: 1 | 2 | 3 | 4;
  location: string | null;
  note: string | null;
}

export interface CreateOpenDutyWorkInput {
  siteId: string;
  callTypeCode: string;
  priority: 1 | 2 | 3 | 4;
  location: string | null;
  note: string | null;
}

export type CreateDutyCallOfferOutcome =
  | { ok: true; callId: string; callRef: string }
  | { ok: false; reason: string };

export type OpenDutyWorkMutationOutcome =
  | { ok: true; callId: string; callRef: string }
  | { ok: false; reason: string };

const DUTY_CALL_STATUSES: ReadonlySet<string> = new Set([
  "open",
  "active",
  "cleared",
  "cancelled",
]);
const DUTY_CALL_ASSIGNMENT_STATES: ReadonlySet<string> = new Set([
  "offered",
  "accepted",
  "declined",
  "cleared",
  "cancelled",
]);
const DUTY_CALL_LINEAR_STATUSES: ReadonlySet<string> = new Set([
  "starting",
  "en_route",
  "on_scene",
]);
// Must track duty_call_status_events_type_known. fetchDutyCallTrail fails the whole read closed
// on an unknown word, so a word the database can already write and this set does not know takes
// the Call's entire history off screen.
const DUTY_CALL_EVENT_TYPES: ReadonlySet<string> = new Set([
  "assigned",
  "pooled",
  "claimed",
  "starting",
  "en_route",
  "on_scene",
  "requesting_backup",
  "declined",
  "incident_attached",
  "cleared",
  "cancelled",
  // Shipped by 20260818220000_cad_assigner_cancel_reoffer.sql, which widened
  // duty_call_status_events_type_known and let the expire-on-touch helper write this word.
  "expired",
]);

function nonemptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function uuidString(value: unknown): string | null {
  const text = nonemptyString(value);
  return text !== null && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(text)
    ? text
    : null;
}

function nullableString(value: unknown): string | null {
  return value === null || value === undefined ? null : nonemptyString(value);
}

function firstObject(value: unknown): Record<string, unknown> | null {
  if (Array.isArray(value)) {
    const first = value[0];
    return typeof first === "object" && first !== null
      ? (first as Record<string, unknown>)
      : null;
  }
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : null;
}

function recordRows(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value)
    ? value.filter(
        (row): row is Record<string, unknown> => typeof row === "object" && row !== null,
      )
    : [];
}

function priorityValue(value: unknown): 1 | 2 | 3 | 4 | null {
  const number = typeof value === "number" ? value : Number.NaN;
  return number === 1 || number === 2 || number === 3 || number === 4 ? number : null;
}

function dutyCallStatus(value: unknown): DutyCallStatus | null {
  return typeof value === "string" && DUTY_CALL_STATUSES.has(value)
    ? (value as DutyCallStatus)
    : null;
}

function dutyCallOrigin(value: unknown): DutyCallOrigin | null {
  return value === "assigned" || value === "officer" ? value : null;
}

function assignmentState(value: unknown): DutyCallAssignmentState | null {
  return typeof value === "string" && DUTY_CALL_ASSIGNMENT_STATES.has(value)
    ? (value as DutyCallAssignmentState)
    : null;
}

function linearStatus(value: unknown): DutyCallLinearStatus | null {
  return typeof value === "string" && DUTY_CALL_LINEAR_STATUSES.has(value)
    ? (value as DutyCallLinearStatus)
    : null;
}

function eventType(value: unknown): DutyCallEventType | null {
  return typeof value === "string" && DUTY_CALL_EVENT_TYPES.has(value)
    ? (value as DutyCallEventType)
    : null;
}

function latestAssignment(value: unknown): Record<string, unknown> | null {
  if (!Array.isArray(value)) return null;
  const assignments = value.filter(
    (item): item is Record<string, unknown> => typeof item === "object" && item !== null,
  );
  assignments.sort((left, right) =>
    (nonemptyString(right.offered_at) ?? "").localeCompare(
      nonemptyString(left.offered_at) ?? "",
    ),
  );
  return assignments[0] ?? null;
}

export function mapDutyCallSummary(raw: Record<string, unknown>): DutyCallSummary | null {
  const callId = nonemptyString(raw.id);
  const callRef = nonemptyString(raw.call_ref);
  const callTypeCode = nonemptyString(raw.call_type_code);
  const priority = priorityValue(raw.priority);
  const status = dutyCallStatus(raw.status);
  const origin = dutyCallOrigin(raw.origin);
  const openPool = typeof raw.open_pool === "boolean" ? raw.open_pool : null;
  const siteId = raw.site_id === null ? null : nonemptyString(raw.site_id);
  const createdAt = nonemptyString(raw.created_at);
  if (
    callId === null ||
    callRef === null ||
    callTypeCode === null ||
    priority === null ||
    status === null ||
    origin === null ||
    openPool === null ||
    (siteId === null && (origin !== "officer" || raw.site_id !== null)) ||
    (openPool && (origin !== "assigned" || status !== "open" || siteId === null)) ||
    createdAt === null
  ) {
    return null;
  }

  const callType = firstObject(raw.call_type);
  const site = firstObject(raw.site);
  const assignment = latestAssignment(raw.assignments);
  const offeredAt = nonemptyString(assignment?.offered_at);
  const state = assignmentState(assignment?.state);

  return {
    callId,
    callRef,
    callTypeCode,
    family: nonemptyString(callType?.family) ?? "Call",
    type: nonemptyString(callType?.label) ?? callTypeCode,
    priority,
    siteId,
    site: siteId === null ? "No site assigned" : nonemptyString(site?.name) ?? "Site unavailable",
    location: siteId === null ? "No route on this shift" : nullableString(raw.location),
    note: nullableString(raw.note),
    origin,
    openPool,
    status,
    assignmentState: state,
    offerExpiresAt: state === "offered" ? nullableString(assignment?.expires_at) : null,
    linearStatus: linearStatus(assignment?.linear_status),
    backupRequestedAt: nullableString(assignment?.backup_requested_at),
    assignedAt: offeredAt ?? createdAt,
    updatedAt: nonemptyString(raw.updated_at) ?? createdAt,
  };
}

/** Workspace open + recent Duty Calls, latest activity first. */
export async function fetchDutyCalls(
  client: SupabaseClient,
  accountId: string,
  limit: number = DUTY_CALL_LIST_LIMIT,
): Promise<DutyCallSummary[]> {
  // Open work gets its own page so a burst of recently cleared history can never evict a
  // still-live Call from Dispatch's dock. The second page is bounded recent history only.
  const [openResult, recentResult] = await Promise.all([
    client
      .from("duty_calls")
      .select(DUTY_CALL_LIST_SELECT)
      .eq("account_id", accountId)
      .in("status", ["open", "active"])
      .order("updated_at", { ascending: false })
      .limit(DUTY_CALL_OPEN_LIMIT),
    client
      .from("duty_calls")
      .select(DUTY_CALL_LIST_SELECT)
      .eq("account_id", accountId)
      .in("status", ["cleared", "cancelled"])
      .order("updated_at", { ascending: false })
      .limit(limit),
  ]);
  if (
    openResult.error !== null ||
    recentResult.error !== null ||
    !Array.isArray(openResult.data) ||
    !Array.isArray(recentResult.data)
  ) {
    throw new Error("Could not load Duty Calls.");
  }
  const callRowsById = new Map<string, Record<string, unknown>>();
  const rawCallRows = [...recordRows(openResult.data), ...recordRows(recentResult.data)];
  if (rawCallRows.length !== openResult.data.length + recentResult.data.length) {
    throw new Error("Could not load Duty Calls.");
  }
  for (const row of rawCallRows) {
    const id = nonemptyString(row.id);
    if (id === null) throw new Error("Could not load Duty Calls.");
    if (!callRowsById.has(id)) callRowsById.set(id, row);
  }
  const callRows = [...callRowsById.values()].sort((left, right) =>
    (nonemptyString(right.updated_at) ?? "").localeCompare(
      nonemptyString(left.updated_at) ?? "",
    ),
  );
  if (callRows.length === 0) return [];

  const callIds = callRows
    .map((row) => nonemptyString(row.id))
    .filter((id): id is string => id !== null);
  const typeCodes = [
    ...new Set(
      callRows
        .map((row) => nonemptyString(row.call_type_code))
        .filter((code): code is string => code !== null),
    ),
  ];
  const siteIds = [
    ...new Set(
      callRows
        .map((row) => nonemptyString(row.site_id))
        .filter((id): id is string => id !== null),
    ),
  ];

  const assignmentRequest = client
    .from("duty_call_assignments")
    .select(DUTY_CALL_ASSIGNMENTS_SELECT)
    .eq("account_id", accountId)
    .in("duty_call_id", callIds);
  const typeRequest = client
    .from("duty_call_types")
    .select(DUTY_CALL_TYPES_SELECT)
    .in("code", typeCodes);
  const siteRequest = siteIds.length === 0
    ? Promise.resolve({ data: [] as unknown[], error: null })
    : client
        .from("sites")
        .select(DUTY_CALL_SITES_SELECT)
        .eq("account_id", accountId)
        .in("id", siteIds);
  const [assignmentResult, typeResult, siteResult] = await Promise.all([
    assignmentRequest,
    typeRequest,
    siteRequest,
  ]);
  if (
    assignmentResult.error !== null ||
    typeResult.error !== null ||
    siteResult.error !== null ||
    !Array.isArray(assignmentResult.data) ||
    !Array.isArray(typeResult.data) ||
    !Array.isArray(siteResult.data)
  ) {
    throw new Error("Could not load Duty Calls.");
  }

  const assignmentsByCall = new Map<string, Record<string, unknown>[]>();
  for (const assignment of recordRows(assignmentResult.data)) {
    const callId = nonemptyString(assignment.duty_call_id);
    if (callId === null) continue;
    const assignments = assignmentsByCall.get(callId) ?? [];
    assignments.push(assignment);
    assignmentsByCall.set(callId, assignments);
  }
  const typesByCode = new Map<string, Record<string, unknown>>();
  for (const callType of recordRows(typeResult.data)) {
    const code = nonemptyString(callType.code);
    if (code !== null) typesByCode.set(code, callType);
  }
  const sitesById = new Map<string, Record<string, unknown>>();
  for (const site of recordRows(siteResult.data)) {
    const id = nonemptyString(site.id);
    if (id !== null) sitesById.set(id, site);
  }

  const calls = callRows
    .map((row) => ({
      ...row,
      call_type: typesByCode.get(nonemptyString(row.call_type_code) ?? "") ?? null,
      site: sitesById.get(nonemptyString(row.site_id) ?? "") ?? null,
      assignments: assignmentsByCall.get(nonemptyString(row.id) ?? "") ?? [],
    }))
    .map(mapDutyCallSummary);
  if (calls.some((call) => call === null)) {
    throw new Error("Could not load Duty Calls.");
  }
  return calls.filter((call): call is DutyCallSummary => call !== null);
}

function mapDisposition(raw: Record<string, unknown>): DutyCallDisposition | null {
  const code = nonemptyString(raw.code);
  const label = nonemptyString(raw.label);
  if (code === null || label === null) return null;
  const shortCode = nullableString(raw.short_code);
  return {
    code,
    label,
    ...(shortCode !== null ? { shortCode } : {}),
  };
}

function positiveFiniteNumber(value: unknown): number | null {
  // PostgREST emits `numeric` unquoted, but tolerate a stringified value the way the map and
  // checkpoint read models already do rather than silently dropping a real accuracy reading.
  if (typeof value === "number") {
    return Number.isFinite(value) && value > 0 ? value : null;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }
  return null;
}

/**
 * Read-model GPS proof for one rung. Coordinate-free: only whether a fix exists and, when the
 * device supplied one, its accuracy.
 *
 * A contradictory row resolves to `unreadable` rather than throwing. GPS is decoration on a rung;
 * the ladder fact itself is still intact, so refusing to render the Call's entire append-only
 * history over one malformed accuracy would deny Dispatch far more proof than it protects. It
 * must not resolve to `absent` either — that would state a fix is missing when one may be stored.
 */
function mapTrailGps(
  raw: Record<string, unknown>,
): { gpsProof: DutyCallGpsProof; gpsAccuracyMeters: number | null } {
  const capturedAbsent = raw.gps_captured_at === null || raw.gps_captured_at === undefined;
  const accuracyAbsent = raw.gps_accuracy_meters === null || raw.gps_accuracy_meters === undefined;
  const capturedAt = capturedAbsent ? null : nonemptyString(raw.gps_captured_at);
  const accuracy = accuracyAbsent ? null : positiveFiniteNumber(raw.gps_accuracy_meters);
  const contradictory =
    // A present capture time that is not a readable instant.
    (!capturedAbsent && (capturedAt === null || !Number.isFinite(Date.parse(capturedAt))))
    // A present accuracy the database's own positive-finite check forbids.
    || (!accuracyAbsent && accuracy === null)
    // Accuracy with no fix behind it: DB-legal, writer-impossible.
    || (!accuracyAbsent && capturedAbsent);
  if (contradictory) return { gpsProof: "unreadable", gpsAccuracyMeters: null };
  return {
    gpsProof: capturedAt === null ? "absent" : "recorded",
    gpsAccuracyMeters: accuracy,
  };
}

function mapTrailEvent(
  raw: Record<string, unknown>,
  dispositions: ReadonlyMap<string, DutyCallDisposition>,
): DutyCallTrailEvent | null {
  const type = eventType(raw.event_type);
  const recordedAt = nonemptyString(raw.recorded_at);
  const actorLabel = nonemptyString(raw.actor_label);
  const eventSeq =
    typeof raw.event_seq === "number" && Number.isSafeInteger(raw.event_seq) && raw.event_seq > 0
      ? raw.event_seq
      : null;
  if (type === null || recordedAt === null || actorLabel === null || eventSeq === null) {
    return null;
  }
  const dispositionCode = nullableString(raw.disposition_code);
  return {
    eventType: type,
    recordedAt,
    actorLabel,
    note: nullableString(raw.note),
    dispositionCode,
    disposition: dispositionCode === null ? null : dispositions.get(dispositionCode) ?? null,
    eventSeq,
    ...mapTrailGps(raw),
  };
}

/** Read the immutable trail only; event_seq is the authoritative newest-first ordering. */
export async function fetchDutyCallTrail(
  client: SupabaseClient,
  accountId: string,
  callId: string,
  limit: number = DUTY_CALL_TRAIL_LIMIT,
): Promise<DutyCallTrailEvent[]> {
  const { data, error } = await client
    .from("duty_call_status_events")
    .select(DUTY_CALL_TRAIL_SELECT)
    .eq("account_id", accountId)
    .eq("duty_call_id", callId)
    .order("event_seq", { ascending: false })
    .limit(limit);
  if (error !== null || !Array.isArray(data)) {
    throw new Error("Could not load the Call status trail.");
  }

  const rawEvents = recordRows(data);
  if (rawEvents.length !== data.length) {
    throw new Error("Could not load the Call status trail.");
  }
  const codes = [
    ...new Set(
      rawEvents
        .map((row) => nullableString(row.disposition_code))
        .filter((code): code is string => code !== null),
    ),
  ];
  const dispositions = new Map<string, DutyCallDisposition>();
  if (codes.length > 0) {
    const result = await client
      .from("duty_call_dispositions")
      .select("code,label,short_code")
      .in("code", codes);
    if (result.error !== null || !Array.isArray(result.data)) {
      throw new Error("Could not load the Call status trail.");
    }
    for (const row of recordRows(result.data)) {
      const disposition = mapDisposition(row);
      if (disposition !== null) dispositions.set(disposition.code, disposition);
    }
  }

  const events: DutyCallTrailEvent[] = [];
  for (const row of rawEvents) {
    const event = mapTrailEvent(row, dispositions);
    if (event === null) {
      // Append-only proof must fail visibly; silently dropping an unparseable rung would
      // present an incomplete history as complete.
      throw new Error("Could not load the Call status trail.");
    }
    events.push(event);
  }
  return events.sort((left, right) => right.eventSeq - left.eventSeq);
}

export function countOpenDutyCalls(calls: readonly Pick<DutyCallSummary, "status">[]): number {
  return calls.filter((call) => call.status === "open" || call.status === "active").length;
}

export function dutyCallEventLabel(type: DutyCallEventType): string {
  switch (type) {
    case "assigned":
      return "Assigned";
    case "pooled":
      return "Open work";
    case "claimed":
      return "Claimed";
    case "starting":
      return "Starting";
    case "en_route":
      return "En route";
    case "on_scene":
      return "On scene";
    case "requesting_backup":
      return "Requesting backup";
    case "declined":
      return "Declined";
    case "incident_attached":
      return "Incident attached";
    case "cleared":
      return "Cleared";
    // Both words end an *assignment*, never the Call. `duty_calls.status` stays `open` through a
    // cancel or an expiry, so a bare "Cancelled" here would read as the Call itself being called
    // off — which v1 cannot even reach.
    case "cancelled":
      return "Offer cancelled";
    case "expired":
      return "Offer expired";
  }
}

/**
 * The only rungs the append-only writer will stamp a fix on. Dispatcher-authored and synthetic
 * rungs (`assigned`, `pooled`, `claimed`, `declined`, `incident_attached`, `cancelled`, `expired`)
 * have no officer device behind them, so an absent fix there is structural, not a missing proof.
 * `incident_attached` is written by a field_incidents trigger with no GPS arguments at all, and
 * `expired` is written by `app_private.expire_overdue_duty_call_offers` with no actor at all.
 *
 * Source of truth is the allowlist inside `app_private.append_duty_call_status_event`
 * (`supabase/migrations/20260817190000_guard_duty_activity_gps_stamps.sql`). Nothing here can
 * detect drift, and drift is wrong in two directions. If a later migration lets another event word
 * carry GPS and this set is not widened in the same change:
 *
 *   - a stamped rung of that word renders `Unreadable` — loud and visible; and
 *   - an *unstamped* rung of that word renders no GPS line at all, which the compact trail's
 *     footnote then explains as "an event the writer never stamps". That is a written falsehood
 *     about a word the writer does stamp, and it is the quieter of the two failures.
 *
 * The test beside this constant compares it to a literal in the same file, so it pins the constant,
 * not the migration. Widening the writer's allowlist means editing this set in the same change.
 */
export const DUTY_CALL_GPS_EVENT_TYPES: ReadonlySet<DutyCallEventType> = new Set([
  "starting",
  "en_route",
  "on_scene",
  "requesting_backup",
  "cleared",
]);

function accuracyMetersLabel(meters: number): string {
  return meters >= 10 ? String(Math.round(meters)) : String(Math.round(meters * 10) / 10);
}

/**
 * Compact, coordinate-free GPS proof for one trail rung.
 *
 * "No GPS stamp" is deliberately a statement about the stored row, not about the officer's
 * device. Every rung written before the GPS columns shipped is null, as is every rung from a
 * mobile build that predates GPS capture, so wording it as a device failure would systematically
 * misstate the whole pre-slice history.
 *
 * "—" means the event word never carries a fix at all. The compact trail no longer renders it —
 * it omits the GPS line entirely for those rungs and lets the footnote say what the absence means
 * — so this arm is retained for a total return type, not for display.
 */
export function dutyCallGpsProofLabel(
  event: Pick<DutyCallTrailEvent, "eventType" | "gpsProof" | "gpsAccuracyMeters">,
): string {
  if (event.gpsProof === "unreadable") return "Unreadable";
  const stampable = DUTY_CALL_GPS_EVENT_TYPES.has(event.eventType);
  if (event.gpsProof === "recorded") {
    // A fix on a rung the writer never stamps is as contradictory as a malformed column.
    if (!stampable) return "Unreadable";
    return event.gpsAccuracyMeters === null
      ? "Recorded"
      : `Recorded · ±${accuracyMetersLabel(event.gpsAccuracyMeters)} m`;
  }
  return stampable ? "No GPS stamp" : "—";
}

/**
 * Newest `cleared` rung, which carries the officer's clear/disposition note verbatim. Reading the
 * clear note from the append-only trail keeps the bounded list query free of `disposition_note`
 * while showing the same text `duty_calls.disposition_note` stores.
 *
 * Deliberately max-by-seq rather than the `.find()` the sibling rung lookups use: those pick any
 * matching rung, this one picks *the* authoritative one. `fetchDutyCallTrail` happens to return
 * newest-first today, and quoting an officer's superseded words because that ordering changed is
 * not a failure worth leaving open.
 */
export function dutyCallClearEvent(
  events: readonly DutyCallTrailEvent[],
): DutyCallTrailEvent | null {
  return events.reduce<DutyCallTrailEvent | null>(
    (latest, event) =>
      event.eventType !== "cleared" || (latest !== null && latest.eventSeq >= event.eventSeq)
        ? latest
        : event,
    null,
  );
}

export function dutyCallSummaryLabel(call: DutyCallSummary): string {
  if (call.status === "cleared") return "Cleared";
  if (call.status === "cancelled") return "Cancelled";
  if (call.openPool) return "Open work";
  if (call.linearStatus !== null) return dutyCallEventLabel(call.linearStatus);
  if (call.assignmentState === "declined") return "Declined";
  // A cancelled or expired offer leaves the Call open with nobody holding it. Before the CAD
  // assigner slice nothing could write `cancelled`, so this arm was unreachable and every
  // pre-ladder Call fell through to "Assigned". It is reachable now, and "Assigned" on a Call with
  // no assignee would name an officer who does not exist. The trail says which of the two ended
  // it; the list row only has to stop claiming somebody holds it.
  if (call.assignmentState === "cancelled") return "Unassigned";
  return "Assigned";
}

function mapCallTypeOption(raw: Record<string, unknown>): DutyCallTypeOption | null {
  const code = nonemptyString(raw.code);
  const family = nonemptyString(raw.family);
  const label = nonemptyString(raw.label);
  const defaultPriority = priorityValue(raw.default_priority);
  return code === null || family === null || label === null || defaultPriority === null
    ? null
    : { code, family, label, defaultPriority };
}

/** Redacted pickers for the thin create-offer form. Member options carry membership ids only. */
export async function fetchDutyCallOfferOptions(
  client: SupabaseClient,
  accountId: string,
): Promise<DutyCallOfferOptions> {
  const [siteResult, memberResult, typeResult] = await Promise.all([
    client
      .from("sites")
      .select("id,name,status")
      .eq("account_id", accountId)
      .eq("active", true)
      .order("name", { ascending: true }),
    client
      .from("management_member_directory")
      .select("membership_id,member_label,member_display_name")
      .eq("account_id", accountId)
      .in("role", ["guard", "supervisor"])
      .eq("status", "active")
      .order("created_at", { ascending: true }),
    client
      .from("duty_call_types")
      .select("code,family,label,default_priority")
      .eq("active", true)
      .order("sort_order", { ascending: true }),
  ]);
  if (
    siteResult.error !== null ||
    memberResult.error !== null ||
    typeResult.error !== null ||
    !Array.isArray(siteResult.data) ||
    !Array.isArray(memberResult.data) ||
    !Array.isArray(typeResult.data)
  ) {
    throw new Error("Could not load the Duty Call offer form.");
  }

  const sites = recordRows(siteResult.data)
    .map((row): DutyCallOfferSite | null => {
      const siteId = nonemptyString(row.id);
      const label = nonemptyString(row.name);
      const status = nonemptyString(row.status);
      return siteId === null || label === null || status === null
        ? null
        : { siteId, label, live: status === "live" };
    })
    .filter((site): site is DutyCallOfferSite => site !== null);
  const members = recordRows(memberResult.data)
    .map((row): DutyCallOfferMember | null => {
      const membershipId = nonemptyString(row.membership_id);
      const label = nonemptyString(row.member_display_name) ?? nonemptyString(row.member_label);
      return membershipId === null || label === null ? null : { membershipId, label };
    })
    .filter((member): member is DutyCallOfferMember => member !== null)
    .sort((left, right) => left.label.localeCompare(right.label));
  const callTypes = recordRows(typeResult.data)
    .map(mapCallTypeOption)
    .filter((callType): callType is DutyCallTypeOption => callType !== null);

  return { sites, members, callTypes };
}

export const SAFE_CREATE_DUTY_CALL_FAILURE =
  "Could not create that Duty Call offer. Please try again.";

const KNOWN_CREATE_DUTY_CALL_FAILURES: ReadonlySet<string> = new Set([
  "Authentication is required to assign a Duty Call.",
  "Account id is required to assign a Duty Call.",
  "Supervisor or platform access is required to assign a Duty Call.",
  "Site id is required to assign a Duty Call.",
  "Assigned officer is required to assign a Duty Call.",
  "Site does not belong to this account.",
  "Assigned officer is not an active field member of this account.",
  "Unknown or inactive Duty Call type.",
  "Duty Call priority must be between 1 and 4.",
  "That officer already has an open Duty Call offer or active Call.",
]);

/** Thin supervisor/platform seed seam. The server resolves membership -> user and reauthorizes. */
export async function createDutyCallOffer(
  client: SupabaseClient,
  accountId: string,
  input: CreateDutyCallOfferInput,
): Promise<CreateDutyCallOfferOutcome> {
  try {
    const { data, error } = await client.rpc("create_duty_call_offer", {
      p_account_id: accountId,
      p_site_id: input.siteId,
      p_call_type_code: input.callTypeCode,
      p_guard_membership_id: input.guardMembershipId,
      p_priority: input.priority,
      p_location: nullableString(input.location),
      p_note: nullableString(input.note),
    });
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return {
        ok: false,
        reason: KNOWN_CREATE_DUTY_CALL_FAILURES.has(message)
          ? message
          : SAFE_CREATE_DUTY_CALL_FAILURE,
      };
    }
    const row = firstObject(data);
    const call = firstObject(row?.call);
    const callId = uuidString(row?.callId);
    const assignmentId = uuidString(row?.assignmentId);
    const callRef = nonemptyString(call?.callRef);
    if (
      callId === null ||
      assignmentId === null ||
      callRef === null ||
      !/^\d{2}-\d{4,}$/.test(callRef) ||
      row?.state !== "offered" ||
      call?.siteId !== input.siteId ||
      call?.typeCode !== input.callTypeCode ||
      call?.priority !== input.priority
    ) {
      return { ok: false, reason: SAFE_CREATE_DUTY_CALL_FAILURE };
    }
    return { ok: true, callId, callRef };
  } catch {
    return { ok: false, reason: SAFE_CREATE_DUTY_CALL_FAILURE };
  }
}

export const SAFE_CREATE_OPEN_DUTY_WORK_FAILURE =
  "Could not create that open work. Please try again.";
export const SAFE_PUT_OPEN_DUTY_WORK_FAILURE =
  "Could not place that Duty Call in Open work. Please try again.";

const KNOWN_CREATE_OPEN_DUTY_WORK_FAILURES: ReadonlySet<string> = new Set([
  "Authentication is required to create open work.",
  "Account id is required to create open work.",
  "Supervisor or platform access is required to create open work.",
  "Choose an active live site in this account.",
  "Unknown or inactive Duty Call type.",
  "Duty Call priority must be between 1 and 4.",
  "Open work location must be 160 characters or fewer.",
  "Open work note must be 500 characters or fewer.",
  "Open work details contain a restricted value.",
]);

const KNOWN_PUT_OPEN_DUTY_WORK_FAILURES: ReadonlySet<string> = new Set([
  "Authentication is required to move a Duty Call to open work.",
  "Account id and Duty Call id are required.",
  "Supervisor or platform access is required to move a Duty Call to open work.",
  "Duty Call is unavailable for open work.",
  "Only an open dispatch Duty Call can move to open work.",
  "Open work requires an active Call type and active live site.",
  "Resolve the current Duty Call offer or active Call before moving it to open work.",
  "Existing Duty Call details exceed Open work limits.",
  "Existing Duty Call details contain a restricted value.",
]);

function parseOpenDutyWorkReceipt(
  data: unknown,
  expected: {
    callId?: string;
    callTypeCode?: string;
    location?: string;
    note?: string;
    priority?: 1 | 2 | 3 | 4;
    siteId?: string;
  },
): { callId: string; callRef: string } | null {
  if (Array.isArray(data) && data.length !== 1) return null;
  const row = firstObject(data);
  const callId = uuidString(row?.callId);
  const callRef = nonemptyString(row?.callRef);
  const siteId = uuidString(row?.siteId);
  const typeCode = nonemptyString(row?.typeCode);
  const priority = priorityValue(row?.priority);
  const family = nonemptyString(row?.family);
  const type = nonemptyString(row?.type);
  const site = nonemptyString(row?.site);
  const pooledAt = nonemptyString(row?.pooledAt);
  const pooledByLabel = nonemptyString(row?.pooledByLabel);
  if (
    callId === null
    || callRef === null
    || !/^\d{2}-\d{4,}$/.test(callRef)
    || siteId === null
    || typeCode === null
    || priority === null
    || family === null
    || type === null
    || site === null
    || typeof row?.location !== "string"
    || typeof row?.note !== "string"
    || pooledAt === null
    || !Number.isFinite(Date.parse(pooledAt))
    || pooledByLabel === null
    || row?.origin !== "assigned"
    || row?.status !== "open"
    || row?.openPool !== true
    || (expected.callId !== undefined && callId !== expected.callId)
    || (expected.siteId !== undefined && siteId !== expected.siteId)
    || (expected.callTypeCode !== undefined && typeCode !== expected.callTypeCode)
    || (expected.location !== undefined && row?.location !== expected.location)
    || (expected.note !== undefined && row?.note !== expected.note)
    || (expected.priority !== undefined && priority !== expected.priority)
  ) {
    return null;
  }
  return { callId, callRef };
}

/** Thin supervisor/platform seed that creates an explicitly claimable Call with no assignment. */
export async function createOpenDutyWork(
  client: SupabaseClient,
  accountId: string,
  input: CreateOpenDutyWorkInput,
): Promise<OpenDutyWorkMutationOutcome> {
  try {
    const { data, error } = await client.rpc("create_open_duty_work", {
      p_account_id: accountId,
      p_site_id: input.siteId,
      p_call_type_code: input.callTypeCode,
      p_priority: input.priority,
      p_location: nullableString(input.location),
      p_note: nullableString(input.note),
    });
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return {
        ok: false,
        reason: KNOWN_CREATE_OPEN_DUTY_WORK_FAILURES.has(message)
          ? message
          : SAFE_CREATE_OPEN_DUTY_WORK_FAILURE,
      };
    }
    const receipt = parseOpenDutyWorkReceipt(data, {
      callTypeCode: input.callTypeCode,
      location: nullableString(input.location) ?? "",
      note: nullableString(input.note) ?? "",
      priority: input.priority,
      siteId: input.siteId,
    });
    return receipt === null
      ? { ok: false, reason: SAFE_CREATE_OPEN_DUTY_WORK_FAILURE }
      : { ok: true, ...receipt };
  } catch {
    return { ok: false, reason: SAFE_CREATE_OPEN_DUTY_WORK_FAILURE };
  }
}

/** Thin supervisor/platform transition for an eligible existing dispatch Call. */
export async function putDutyCallInOpenPool(
  client: SupabaseClient,
  accountId: string,
  callId: string,
): Promise<OpenDutyWorkMutationOutcome> {
  try {
    const { data, error } = await client.rpc("put_duty_call_in_open_pool", {
      p_account_id: accountId,
      p_duty_call_id: callId,
    });
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return {
        ok: false,
        reason: KNOWN_PUT_OPEN_DUTY_WORK_FAILURES.has(message)
          ? message
          : SAFE_PUT_OPEN_DUTY_WORK_FAILURE,
      };
    }
    const receipt = parseOpenDutyWorkReceipt(data, { callId });
    return receipt === null
      ? { ok: false, reason: SAFE_PUT_OPEN_DUTY_WORK_FAILURE }
      : { ok: true, ...receipt };
  } catch {
    return { ok: false, reason: SAFE_PUT_OPEN_DUTY_WORK_FAILURE };
  }
}

export const SAFE_CANCEL_DUTY_CALL_OFFER_FAILURE =
  "Could not cancel that Duty Call offer. Please try again.";
export const SAFE_REOFFER_DUTY_CALL_FAILURE =
  "Could not re-offer that Duty Call. Please try again.";

const KNOWN_CANCEL_DUTY_CALL_OFFER_FAILURES: ReadonlySet<string> = new Set([
  "Authentication is required to cancel a Duty Call offer.",
  "Account id and Duty Call id are required.",
  "Supervisor or platform access is required to cancel a Duty Call offer.",
  "Duty Call offer is unavailable.",
  "An accepted Duty Call cannot be cancelled by Dispatch.",
  "This Duty Call has no unanswered offer to cancel.",
]);

const KNOWN_REOFFER_DUTY_CALL_FAILURES: ReadonlySet<string> = new Set([
  "Authentication is required to re-offer a Duty Call.",
  "Account id and Duty Call id are required.",
  "Assigned officer is required to re-offer a Duty Call.",
  "Supervisor or platform access is required to re-offer a Duty Call.",
  "Only an open dispatch Duty Call can be re-offered.",
  "An accepted Duty Call cannot be re-offered.",
  "Assigned officer is not an active field member of this account.",
  "That officer already has an open Duty Call offer or active Call.",
]);

export type CancelDutyCallOfferOutcome =
  | { ok: true; callId: string; callRef: string }
  | { ok: false; reason: string };

export type ReofferDutyCallOutcome =
  | { ok: true; callId: string; callRef: string }
  | { ok: false; reason: string };

/**
 * Shared receipt shape for the two assignment-level dispatch RPCs. Both return
 * `app_private.duty_call_assignment_read_model`, so the Call identity and the resulting
 * assignment state are proved from the same payload rather than assumed from a 200.
 */
function parseDutyCallAssignmentReceipt(
  data: unknown,
  expected: { callId: string; state: DutyCallAssignmentState },
): { callId: string; callRef: string } | null {
  if (Array.isArray(data) && data.length !== 1) return null;
  const row = firstObject(data);
  const call = firstObject(row?.call);
  const callId = uuidString(row?.callId);
  const callRef = nonemptyString(call?.callRef);
  if (
    callId === null
    || callId !== expected.callId
    || callRef === null
    || !/^\d{2}-\d{4,}$/.test(callRef)
    || row?.state !== expected.state
    // Neither act touches `duty_calls.status`: cancel, expire, and re-offer all leave the Call
    // open. A receipt that says otherwise is not the transition Dispatch asked for.
    || call?.status !== "open"
  ) {
    return null;
  }
  return { callId, callRef };
}

/**
 * Dispatch safety valve for an unanswered offer. The Call stays `open` and the officer's
 * one-open-Call slot frees, so the same Call can be re-offered or placed in Open work.
 *
 * A cancel that races the account's own expire-on-touch is not an error: the server returns the
 * assignment it expired, in the same `cancelled` state, and Dispatch gets the outcome it asked
 * for either way.
 */
export async function cancelDutyCallOffer(
  client: SupabaseClient,
  accountId: string,
  callId: string,
): Promise<CancelDutyCallOfferOutcome> {
  try {
    const { data, error } = await client.rpc("cancel_duty_call_offer", {
      p_account_id: accountId,
      p_duty_call_id: callId,
    });
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return {
        ok: false,
        reason: KNOWN_CANCEL_DUTY_CALL_OFFER_FAILURES.has(message)
          ? message
          : SAFE_CANCEL_DUTY_CALL_OFFER_FAILURE,
      };
    }
    const receipt = parseDutyCallAssignmentReceipt(data, { callId, state: "cancelled" });
    return receipt === null
      ? { ok: false, reason: SAFE_CANCEL_DUTY_CALL_OFFER_FAILURE }
      : { ok: true, ...receipt };
  } catch {
    return { ok: false, reason: SAFE_CANCEL_DUTY_CALL_OFFER_FAILURE };
  }
}

/**
 * Re-offer the *same* Call to a different officer. Not a create: no second `call_ref`, no second
 * `duty_calls` row, and the Call's create note and Incident receipts stay attached.
 *
 * Uses the membership-id overload (`p_by_membership`) so this module never handles an auth user
 * id. The boolean is only a Postgres signature discriminator against the user-id 3-arg RPC.
 */
export async function reofferDutyCall(
  client: SupabaseClient,
  accountId: string,
  callId: string,
  guardMembershipId: string,
): Promise<ReofferDutyCallOutcome> {
  try {
    const { data, error } = await client.rpc("reoffer_duty_call", {
      p_account_id: accountId,
      p_duty_call_id: callId,
      p_guard_membership_id: guardMembershipId,
      p_by_membership: true,
    });
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return {
        ok: false,
        reason: KNOWN_REOFFER_DUTY_CALL_FAILURES.has(message)
          ? message
          : SAFE_REOFFER_DUTY_CALL_FAILURE,
      };
    }
    const receipt = parseDutyCallAssignmentReceipt(data, { callId, state: "offered" });
    return receipt === null
      ? { ok: false, reason: SAFE_REOFFER_DUTY_CALL_FAILURE }
      : { ok: true, ...receipt };
  } catch {
    return { ok: false, reason: SAFE_REOFFER_DUTY_CALL_FAILURE };
  }
}
